import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../services/authContext';

interface ReportCard {
  title: string;
  description: string;
  path: string;
  icon: string;
  requiresAdmin: boolean;
}

const financialReports: ReportCard[] = [
  {
    // P1 Finance (ISO gap-analysis). requiresAdmin:true mirrors the Payroll /
    // Billability tiles. It does NOT hide the tile from finance —
    // isTileVisible admits admin OR finance (F-1406) — it keeps the tile away
    // from everyone else; /reports/revenue is gated by <FinanceRoute>.
    title: 'Monthly Revenue',
    description: 'Invoiced revenue by month vs. the monthly target (CZK)',
    path: '/reports/revenue',
    icon: '💹',
    requiresAdmin: true,
  },
  {
    title: 'Detailed Project Report',
    description:
      'Comprehensive project analysis with billing by rate, expenses, extra gains, and XLS export',
    path: '/reports/project/detailed',
    icon: '📊',
    requiresAdmin: false,
  },
  {
    title: 'Payroll Report',
    description:
      'Hours and cost breakdown per user including user costs, extra costs, assignment costs, and XLS export',
    path: '/reports/payroll',
    icon: '💰',
    requiresAdmin: true,
  },
  {
    // Quick task 260428-lw0: Project Report summary is now a manager/PM surface
    // (scoped server-side), matching the Detailed Project Report tile. Marked
    // requiresAdmin:false so non-admin managers/PMs see the tile; admin + finance
    // still see it via the isAdmin/isFinance branch in isTileVisible.
    title: 'Project Report',
    description: 'Hours, billing, and costs breakdown by project',
    path: '/reports/project',
    icon: '📁',
    requiresAdmin: false,
  },
  {
    title: 'Attendance Report',
    description: 'Hours logged per user, days worked, and daily breakdown',
    path: '/reports/attendance',
    icon: '⏱️',
    requiresAdmin: true,
  },
  {
    title: 'Invoicing Status',
    description: 'Track invoiced vs uninvoiced billable entries by period',
    path: '/reports/invoicing-status',
    icon: '📄',
    requiresAdmin: true,
  },
  {
    title: 'Freelancer Invoices',
    description: 'Cross-check expected freelancer invoices by month',
    path: '/reports/freelancer-invoices',
    icon: '🧾',
    requiresAdmin: true,
  },
  // Phase 57 — Billability Report. `requiresAdmin: true` mirrors Payroll
  // Report; finance still sees the tile through isTileVisible's isFinance
  // branch, and /reports/billability is gated by <FinanceRoute>.
  {
    title: 'Billability Report',
    description:
      'Per-user billable vs. non-billable hours with cost, revenue, and margin for the selected month',
    path: '/reports/billability',
    icon: '📈',
    requiresAdmin: true,
  },
];

// MKS-332 — the money glossary. `requiresAdmin: false`: the whole point is
// that anyone looking at a money figure can look up what it means, and the
// page itself hides the Cost and Margin cards from anyone who is not admin or
// finance. Kept out of `financialReports` so the release flag gates exactly
// this one tile.
const moneyGlossaryReport: ReportCard = {
  title: 'Money glossary',
  description: 'What Earned, Invoiced and Paid mean, and how cost and margin are computed',
  path: '/reports/money-glossary',
  icon: '📖',
  requiresAdmin: false,
};

const marketingReports: ReportCard[] = [
  {
    title: 'Web Analytics',
    description: 'Product website traffic, traffic sources, engagement, and conversions from GA4',
    path: '/reports/web-analytics',
    icon: '🌐',
    requiresAdmin: true,
  },
];

// Phase 32 Plan 32-2 — Engineering reports card per UI-SPEC §144-146.
// Persona UAT 2026-05-14 — Petra requested admin-only for now; PMs and
// managers see the report tile hidden until the product decides whether
// they should ingest engineering activity beyond their own projects.
const engineeringReports: ReportCard[] = [
  {
    title: 'Engineering Metrics',
    description:
      'Per-engineer commit activity, AI-assisted share, and automation noise across linked GitHub repositories',
    path: '/reports/engineering',
    icon: '🛠️',
    requiresAdmin: true,
  },
];

function ReportCardButton({ report }: { report: ReportCard }) {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => navigate(report.path)}
      className="bg-white rounded-lg border border-gray-200 p-6 text-left hover:border-primary-300 hover:shadow-sm transition-all flex gap-4 items-start"
    >
      <span className="text-2xl mt-0.5">{report.icon}</span>
      <div>
        <h3 className="text-lg font-medium text-gray-900">{report.title}</h3>
        <p className="mt-1 text-sm text-gray-500">{report.description}</p>
      </div>
    </button>
  );
}

/**
 * F-1406: show a tile when the user is admin OR finance, OR the report
 * doesn't require elevated access at all. Finance is a first-class audience
 * for every money/cost/margin report (Petra, MKS-330), so `requiresAdmin`
 * means "admin or finance", not "admin only".
 *
 * MKS-330: this function was already correct, but the tiles it returned were
 * unreachable — the /reports route itself was admin/manager/PM-gated, so a
 * finance-only user never rendered this page. The guard now admits finance
 * (App.tsx), which is what makes these tiles actually show up.
 */
function isTileVisible(report: ReportCard, isAdmin: boolean, isFinance: boolean): boolean {
  return isAdmin || isFinance || !report.requiresAdmin;
}

export default function ReportsPage() {
  const { isAdmin, isFinance, isManager, isMarketing, isSales } = useAuth();
  const visibleFinancial = [...financialReports, moneyGlossaryReport].filter((r) =>
    isTileVisible(r, isAdmin, isFinance)
  );
  // Phase 85 Plan 02 (MKS-147 / D-06): the Web Analytics tile gets its own
  // dedicated visibility check instead of routing through isTileVisible.
  // isTileVisible only knows isAdmin || isFinance || !requiresAdmin, so
  // clearing requiresAdmin would expose the tile to every authenticated
  // user (RESEARCH Pitfall 1) — webAnalyticsVisible keeps requiresAdmin
  // intact while widening access to marketing and sales.
  const webAnalyticsVisible = isAdmin || isMarketing || isSales;
  const visibleMarketing = marketingReports.filter((r) =>
    r.path === '/reports/web-analytics' ? webAnalyticsVisible : isTileVisible(r, isAdmin, isFinance)
  );
  const visibleEngineering = engineeringReports.filter((r) => isTileVisible(r, isAdmin, isFinance));

  // Phase 28 / MKS-111: PM-only landing copy per UX-BRIEF S7.
  const showPmIntro = isManager && !isAdmin;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
        {showPmIntro && (
          <p className="mt-1 text-sm text-gray-600">Hours reported by the team on your projects.</p>
        )}
      </div>

      {/* Financial & Operations */}
      <div>
        <h2 className="text-sm font-semibold uppercase text-gray-400 mb-3">
          {showPmIntro ? 'Your Projects' : 'Finance & Operations'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {visibleFinancial.map((report) => (
            <ReportCardButton key={report.path} report={report} />
          ))}
        </div>
      </div>

      {visibleMarketing.length > 0 && (
        <>
          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* Marketing */}
          <div>
            <h2 className="text-sm font-semibold uppercase text-gray-400 mb-3">Marketing</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {visibleMarketing.map((report) => (
                <ReportCardButton key={report.path} report={report} />
              ))}
            </div>
          </div>
        </>
      )}

      {visibleEngineering.length > 0 && (
        <>
          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* Engineering — Phase 32 Plan 32-2 */}
          <div>
            <h2 className="text-sm font-semibold uppercase text-gray-400 mb-3">Engineering</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {visibleEngineering.map((report) => (
                <ReportCardButton key={report.path} report={report} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
